import type {
  CardView,
  CategorySelection,
  Pill,
  PillOption,
} from 'features/libra/dashboard/ticketDashboard/types'

export interface IndexedCard {
  card: CardView
  valuesByCategory: Map<string, Set<string>>
  /** Lowercased card title, used by the search box. */
  text: string
}

export interface CardIndex {
  rows: IndexedCard[]
  /** Every value seen per category, with the first label and tone seen for it. */
  seen: Map<string, Map<string, PillOption>>
}

/**
 * Pills a card shows and is matched on: its headline ticket's, then its own.
 * Ticket pills lead so the built-ins (stage, version, author) come first.
 */
export function getCardPills(card: CardView): ReadonlyArray<Pill> {
  return [...card.tickets[0].pills, ...card.pills]
}

export function buildCardIndex(cards: ReadonlyArray<CardView>): CardIndex {
  const seen = new Map<string, Map<string, PillOption>>()

  const rows = cards.map((card) => {
    const pills = getCardPills(card)
    const valuesByCategory = new Map<string, Set<string>>()
    for (const pill of pills) {
      valuesByCategory.set(
        pill.category,
        (valuesByCategory.get(pill.category) ?? new Set<string>()).add(
          pill.value
        )
      )
      const categorySeen =
        seen.get(pill.category) ?? new Map<string, PillOption>()
      if (!categorySeen.has(pill.value)) {
        categorySeen.set(pill.value, {
          value: pill.value,
          label: pill.label,
          tone: pill.tone,
        })
      }
      seen.set(pill.category, categorySeen)
    }
    return { card, valuesByCategory, text: card.title.toLowerCase() }
  })

  return { rows, seen }
}

/** True when, for every category with a selection, the row has at least one selected value. */
export function matchesSelection(
  row: IndexedCard,
  selection: CategorySelection
): boolean {
  return Object.entries(selection).every(
    ([category, selected]) =>
      !selected?.length ||
      selected.some((value) => row.valuesByCategory.get(category)?.has(value))
  )
}
